/**
 * utils/cryptoHash.ts
 * Cryptographic hashing utilities for binary data
 *
 * Provides SHA-256 hashing for ArrayBuffer data using the Web Crypto API.
 *
 * BROWSER-ONLY: Requires secure context (HTTPS or localhost) for crypto.subtle.
 * Returns null on mobile browsers where crypto.subtle is unavailable.
 * Tests using JSDOM/Node will need to mock this module.
 */

/**
 * Check if Web Crypto API (crypto.subtle) is available.
 * crypto.subtle requires a secure context (HTTPS) and isn't available
 * on all mobile browsers (e.g., iOS Chrome/Safari in certain contexts).
 */
const isCryptoSubtleAvailable = (): boolean => {
  try {
    return (
      typeof crypto !== 'undefined' &&
      typeof crypto.subtle?.digest === 'function'
    );
  } catch {
    return false;
  }
};

/** Flag to prevent repeated console warnings */
let hasWarnedCryptoUnavailable = false;

/**
 * Compute SHA-256 hash of binary data
 *
 * Uses the Web Crypto API to generate a cryptographic hash of the provided
 * ArrayBuffer. Returns a lowercase hexadecimal string (64 characters).
 *
 * Returns null if crypto.subtle is unavailable (e.g., on some mobile browsers).
 * Callers should handle null by omitting the hash field rather than storing null.
 *
 * @param data - Binary data to hash
 * @returns Promise resolving to lowercase hexadecimal hash string, or null if unavailable
 *
 * @example
 * const buffer = await file.arrayBuffer();
 * const hash = await sha256Binary(buffer);
 * if (hash) {
 *   // Use hash for deduplication
 * } else {
 *   // crypto.subtle unavailable, skip deduplication
 * }
 */
export async function sha256Binary(data: ArrayBuffer): Promise<string | null> {
  if (!isCryptoSubtleAvailable()) {
    if (!hasWarnedCryptoUnavailable) {
      console.warn(
        'crypto.subtle unavailable - hash computation skipped. Image deduplication disabled on this device.'
      );
      hasWarnedCryptoUnavailable = true;
    }
    return null;
  }

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  return hashHex;
}
